// Presentation helpers for the ski v3 detail page (consumer inventory 5.7, contract
// Sections 14 to 17). Pure and client safe: no I/O, no clock. Everything here reads fields the
// readers already expose (SkiDetail.skiV3BySport, the day's score_provenance, stored lean
// hourly points). Every helper returns null or an empty value when the v3 fields are absent,
// so revision 2 rows keep today's UI.
//
// Copy rules: British English, no em dashes, plain words. Labels follow the published
// vocabularies (lib/conditions/ski-labels-v3.ts).
import {
  skiV3FromProvenance,
  skiV3IsClosed,
  type SkiOffPisteRose,
  type SkiV3BySport,
  type SkiV3Extent,
  type SkiV3SportFields,
  type StoredSkiBand,
  type StoredTimeBlock,
} from "@/lib/conditions/ski-v3-carriers";
import {
  SKI_LABEL_CLOSED,
  SKI_LABEL_NO_LIFT_ACCESS,
  SKI_LABEL_UNAVAILABLE,
  skiLabel100,
} from "@/lib/conditions/ski-labels-v3";
import { SKI_V3_PARAMS } from "@/lib/conditions/ski/params";
import type { AspectSector, ConfidenceClass, SkiSport } from "@/lib/conditions/ski/types";
import { coverWord, isInt100, surfaceWord, type SkiV3Flag } from "./ski-v3-words";

export const SKI_SPORTS: readonly SkiSport[] = ["piste", "off-piste", "touring"];

export function isSkiSportKey(sport: string): sport is SkiSport {
  return sport === "piste" || sport === "off-piste" || sport === "touring";
}

// Words live in ./ski-v3-words (no runtime imports), re-exported here.
export {
  SKI_CONFIDENCE_WORDS,
  SKI_COVER_WORDS,
  SKI_FLAG_CHIPS,
  SKI_SURFACE_WORDS,
  confidenceClassWord,
  coverWord,
  flagChipLabels,
  likelyRangeText,
  surfaceWord,
  type SkiCoverKey,
  type SkiSurfaceClass,
  type SkiV3Flag,
} from "./ski-v3-words";

// ---------------------------------------------------------------------------
// Times and surface sentences
// ---------------------------------------------------------------------------

/** 8 reads "08:00", 10.5 reads "10:30" (local decimal hours). */
export function formatClock(hour: number): string {
  if (!Number.isFinite(hour)) return "";
  const clamped = Math.max(0, Math.min(24, hour));
  let h = Math.floor(clamped);
  let m = Math.round((clamped - h) * 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Surface time blocks as one sentence: "08:00 to 10:00 firm, 10:30 to 12:30 corn, 13:00
 * onwards slush". The last of several blocks reads "onwards"; a single block keeps its end.
 */
export function surfaceBlocksSentence(blocks: readonly StoredTimeBlock[] | null | undefined): string | null {
  if (!blocks?.length) return null;
  const parts = blocks.flatMap((block, index) => {
    const word = surfaceWord(block[2]);
    if (!word) return [];
    if (index === blocks.length - 1 && blocks.length > 1) return [`${formatClock(block[0])} onwards ${word}`];
    return [`${formatClock(block[0])} to ${formatClock(block[1])} ${word}`];
  });
  return parts.length ? parts.join(", ") : null;
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type SkiStatusView = Readonly<{
  sport: SkiSport;
  /** "Closed" or "No lift access". */
  label: string;
  /** One plain sentence on why. */
  reason: string;
  /** Off-piste at a closed resort: touring needs no lifts. */
  pointToTouring: boolean;
}>;

function reasonText(sport: SkiSport, fields: SkiV3SportFields): string {
  if (fields.status === "out-of-season" || fields.statusReason === "season") return "The resort is closed for the season.";
  switch (fields.statusReason) {
    case "resort-closed":
      return "The resort is closed today.";
    case "lifts-closed":
      return sport === "piste" ? "The lifts are closed, so no pistes are open." : "The lifts serving this terrain are closed.";
    case "run-reports":
      return sport === "piste" ? "Run reports show the pistes closed." : "Run reports show this terrain closed.";
    case "no-lift-access":
      return "The lifts are not running, so there is no lift access to off-piste terrain today.";
    default:
      return "Closed today.";
  }
}

/** Closed or no-lift-access state for one sport, or null when the sport is open or has no v3 status. */
export function skiStatusView(sport: SkiSport, fields: SkiV3SportFields | null | undefined): SkiStatusView | null {
  if (!fields || !skiV3IsClosed(fields, sport)) return null;
  const noLift = fields.statusReason === "no-lift-access";
  return {
    sport,
    label: skiLabel100(sport, null, fields.status, fields.statusReason),
    reason: reasonText(sport, fields),
    pointToTouring: noLift && sport === "off-piste",
  };
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

/** Today's score provenance: the first forecast day's own block (the v3 writer stores one per day). */
export function todayScoreProvenance(detail: { forecast?: readonly { score_provenance?: unknown }[] | null } | null | undefined): unknown {
  return detail?.forecast?.[0]?.score_provenance;
}

export type SkiV3View = Readonly<{
  fields: SkiV3SportFields;
  cls: ConfidenceClass | null;
  lo: number | null;
  hi: number | null;
  flags: readonly SkiV3Flag[];
  quality: number | null;
  status: SkiStatusView | null;
}>;

/**
 * Everything the page needs about one ski sport's v3 output, or null when the sport has no
 * v3 fields (revision 2 rows). Flags and the piste quality before extent come from the
 * carriers (SkiV3SportFields.flags and .quality). `provenance`, the day's score_provenance
 * (forecast[0]), is read through the same carriers parser only when `fields` carries neither.
 *
 * A withheld sport (Closed, or No lift access) has no likely range and no quality: the engine
 * keeps its "if open" values on the sport day, but the owner decision is that Closed shows no
 * number, so `lo`, `hi` and `quality` are null whenever `status` is set.
 */
export function skiV3View(
  sport: SkiSport,
  fields: SkiV3SportFields | null | undefined,
  provenance?: unknown,
): SkiV3View | null {
  if (!fields || Object.keys(fields).length === 0) return null;
  const cls = fields.cls ?? null;
  const status = skiStatusView(sport, fields);
  const fallback = fields.flags === undefined && fields.quality === undefined && provenance !== undefined
    ? skiV3FromProvenance(provenance, sport)
    : null;
  const flags: readonly SkiV3Flag[] = fields.flags ?? fallback?.flags ?? [];
  const quality = fields.quality ?? fallback?.quality;
  return {
    fields,
    cls,
    lo: !status && isInt100(fields.lo) ? fields.lo : null,
    hi: !status && isInt100(fields.hi) ? fields.hi : null,
    flags,
    quality: !status && isInt100(quality) ? quality : null,
    status,
  };
}

// ---------------------------------------------------------------------------
// Extent (piste)
// ---------------------------------------------------------------------------

export type SkiExtentSummary = Readonly<{
  /** "Limited terrain: 40 % open (55 km). Score reduced by 12 %." */
  line: string;
  reductionPct: number;
  /** Quality on open runs, 0 to 100, when known. */
  quality: number | null;
  /** The explanation behind the info disclosure. */
  detail: string;
}>;

function formatKm(km: number): string {
  return km >= 10 ? String(Math.round(km)) : String(Math.round(km * 10) / 10);
}

/**
 * The limited-terrain line under the piste headline, or null when the extent factor is 1 (or
 * absent). `quality` is the stored quality before extent; without it the quality is recovered
 * from the published headline (headline = quality x factor, contract Section 14).
 */
export function skiExtentSummary(
  extent: SkiV3Extent | null | undefined,
  quality: number | null,
  headline100: number | null,
): SkiExtentSummary | null {
  const factor = extent?.factor;
  if (typeof factor !== "number" || !Number.isFinite(factor) || factor >= 1 || factor <= 0) return null;
  const reductionPct = Math.round((1 - factor) * 100);
  if (reductionPct < 1) return null;
  const pct = typeof extent?.openPct === "number" && Number.isFinite(extent.openPct)
    ? Math.max(0, Math.min(100, Math.round(extent.openPct)))
    : null;
  const km = typeof extent?.openKm === "number" && Number.isFinite(extent.openKm) && extent.openKm >= 0 ? extent.openKm : null;
  const open = pct !== null
    ? `${pct} % open${km !== null ? ` (${formatKm(km)} km)` : ""}`
    : km !== null ? `${formatKm(km)} km open` : null;
  const line = `Limited terrain${open ? `: ${open}` : ""}. Score reduced by ${reductionPct} %.`;
  const recovered = headline100 !== null && Number.isFinite(headline100)
    ? Math.max(0, Math.min(100, Math.round(headline100 / factor)))
    : null;
  const q = quality ?? recovered;
  const lowered = headline100 !== null && Number.isFinite(headline100) ? Math.round(headline100) : null;
  const detail = q !== null
    ? `Quality on open runs was ${q}. Limited choice lowers the headline${lowered !== null ? ` to ${lowered}` : ""}, by at most a quarter. How good the open runs are is rated separately and does not change.`
    : "The score rates the snow on the runs that are open. Limited choice lowers the headline, by at most a quarter.";
  return { line, reductionPct, quality: q, detail };
}

// ---------------------------------------------------------------------------
// Bands
// ---------------------------------------------------------------------------

export type SkiBandSportCell = Readonly<{ score: number | null; label: string }>;

export type SkiBandRow = Readonly<{
  key: string;
  label: string;
  elevationM: number;
  cover: string | null;
  /** Piste only: share of runs open in the band, 0 to 100. */
  openPct: number | null;
  sports: Readonly<Record<SkiSport, SkiBandSportCell | null>>;
  /** Piste surface blocks as one sentence, or the dominant surface ("Mostly groomed"). */
  surface: string | null;
}>;

const BAND_WORDS: Readonly<Record<string, string>> = { base: "Base", mid: "Mid", summit: "Summit" };

/** Base, Mid, Summit; for 5 bands the in-between keys read "Lower mid" and "Upper mid". */
export function bandLabel(key: string, keys: readonly string[]): string {
  if (BAND_WORDS[key]) return BAND_WORDS[key];
  const index = keys.indexOf(key);
  const mid = keys.indexOf("mid");
  if (mid >= 0 && index >= 0) return index < mid ? "Lower mid" : "Upper mid";
  const n = /^b(\d+)$/.exec(key)?.[1];
  return n ? `Band ${n}` : key;
}

function bandCell(sport: SkiSport, band: StoredSkiBand | undefined): SkiBandSportCell | null {
  if (!band) return null;
  if (band.st === "no_lift_access") return { score: null, label: SKI_LABEL_NO_LIFT_ACCESS };
  if (band.st) return { score: null, label: SKI_LABEL_CLOSED };
  if (band.s === null) return { score: null, label: SKI_LABEL_UNAVAILABLE };
  return { score: band.s, label: skiLabel100(sport, band.s) };
}

/** "72 Great" for a scored band, "Closed" or "No lift access" for a withheld one. */
export function skiLabelForBand(sport: SkiSport, band: StoredSkiBand): string {
  const cell = bandCell(sport, band);
  if (!cell) return SKI_LABEL_UNAVAILABLE;
  return cell.score === null ? cell.label : `${cell.score} ${cell.label}`;
}

/** One row per band (lowest first) with the three sports' band scores, cover and surface. */
export function skiBandRows(bySport: SkiV3BySport | null | undefined): SkiBandRow[] {
  if (!bySport) return [];
  const spine = bySport.piste?.bands ?? bySport["off-piste"]?.bands ?? bySport.touring?.bands;
  if (!spine?.length) return [];
  const keys = spine.map((band) => band.b);
  return spine.map((band) => {
    const find = (sport: SkiSport) => bySport[sport]?.bands?.find((b) => b.b === band.b);
    const piste = find("piste");
    const top = piste?.srf?.[0]?.[0];
    const topWord = surfaceWord(top);
    return {
      key: band.b,
      label: bandLabel(band.b, keys),
      elevationM: band.z,
      cover: coverWord((piste ?? band).cov),
      openPct: typeof piste?.ext === "number" ? piste.ext : null,
      sports: {
        piste: bandCell("piste", piste),
        "off-piste": bandCell("off-piste", find("off-piste")),
        touring: bandCell("touring", find("touring")),
      },
      surface: surfaceBlocksSentence(piste?.blk) ?? (topWord ? `Mostly ${topWord}` : null),
    };
  });
}

/** Cover word per band for the snow cover strip (lowest first), from one sport's bands. */
export function bandCoverStrip(bands: readonly StoredSkiBand[] | null | undefined): { label: string; cover: string }[] {
  if (!bands?.length) return [];
  const keys = bands.map((b) => b.b);
  return bands.flatMap((band) => {
    const cover = coverWord(band.cov);
    return cover ? [{ label: bandLabel(band.b, keys), cover }] : [];
  });
}

/** The headline cover word for a sport: the mid band's, else the middle band's. */
export function headlineCover(bands: readonly StoredSkiBand[] | null | undefined): string | null {
  if (!bands?.length) return null;
  const mid = bands.find((b) => b.b === "mid") ?? bands[Math.floor((bands.length - 1) / 2)];
  return coverWord(mid?.cov);
}

// ---------------------------------------------------------------------------
// Off-piste aspect rose
// ---------------------------------------------------------------------------

export const ROSE_ASPECTS: readonly AspectSector[] = ["N", "E", "S", "W"];
export const ASPECT_WORDS: Readonly<Record<AspectSector, string>> = { N: "North", E: "East", S: "South", W: "West" };

export type SkiRoseBand = Readonly<{
  key: string;
  label: string;
  sectors: readonly { aspect: AspectSector; score: number }[];
}>;

const DEFAULT_BAND_ORDER = ["base", "b2", "mid", "b4", "summit"];

/** Rose rows per band, lowest band first, following the stored band order when known. */
export function skiRoseBands(rose: SkiOffPisteRose | null | undefined, bands?: readonly StoredSkiBand[] | null): SkiRoseBand[] {
  if (!rose) return [];
  const known: string[] = bands?.map((b): string => b.b) ?? [];
  const keys = Object.keys(rose).sort((a, b) => {
    const ia = known.includes(a) ? known.indexOf(a) : 100 + DEFAULT_BAND_ORDER.indexOf(a);
    const ib = known.includes(b) ? known.indexOf(b) : 100 + DEFAULT_BAND_ORDER.indexOf(b);
    return ia - ib || a.localeCompare(b);
  });
  const labelKeys = known.length ? known : keys;
  return keys.flatMap((key) => {
    const entry = rose[key];
    const sectors = ROSE_ASPECTS.flatMap((aspect) => {
      const v = entry?.[aspect];
      return isInt100(v) ? [{ aspect, score: v }] : [];
    });
    return sectors.length ? [{ key, label: bandLabel(key, labelKeys), sectors }] : [];
  });
}

// ---------------------------------------------------------------------------
// Touring plan
// ---------------------------------------------------------------------------

/** The contract's wet-snow onset: the time the wet-snow index reaches this level (Section 12). */
export const WET_SNOW_WARNING_INDEX: number = SKI_V3_PARAMS.touring.wetLevel;

/** "N, E, then S" for an aspect order. */
export function aspectOrderText(aspects: readonly AspectSector[]): string | null {
  const words = aspects.filter((a, i) => aspects.indexOf(a) === i);
  if (!words.length) return null;
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(", ")}, then ${words[words.length - 1]}`;
}

export function formatMetres(m: number): string {
  return `${Math.round(m).toLocaleString("en-GB")} m`;
}

/**
 * The touring wet-snow ceiling at a wet index `w`: 100 - 82 w (engine touring.ts ceilingAt,
 * params ceilingTop and ceilingSpan).
 */
export function wetSnowCeiling100(w: number): number {
  const { ceilingTop, ceilingSpan } = SKI_V3_PARAMS.touring;
  return ceilingTop - ceilingSpan * Math.max(0, Math.min(1, w));
}

/** Points below the ceiling at which the published score still counts as held at it (rounding). */
const CEILING_TOLERANCE = 2;

/**
 * The wet-snow warning, when wet snow is likely by the descent. The writer stores the wet-snow
 * index at the descent time (0 to 1); a value above 1 is read as a height in metres. The copy
 * says the score is capped only when the published touring score (0 to 100) sits at the
 * ceiling (100 - 82 w); below it the ceiling is not what holds the score down.
 */
export function wetSnowWarning(ceiling: number | null | undefined, touring100: number | null = null): string | null {
  if (typeof ceiling !== "number" || !Number.isFinite(ceiling) || ceiling < 0) return null;
  if (ceiling > 1) {
    return `Wet snow likely below about ${formatMetres(ceiling)} by the descent. Plan to be down before it softens.`;
  }
  if (ceiling < WET_SNOW_WARNING_INDEX) return null;
  const capped = touring100 !== null && Number.isFinite(touring100) &&
    touring100 >= wetSnowCeiling100(ceiling) - CEILING_TOLERANCE;
  return capped
    ? "Wet snow likely by the descent time, so the touring score is capped. Start earlier or choose a colder aspect."
    : "Wet snow likely by the descent time. Start earlier or choose a colder aspect.";
}

// ---------------------------------------------------------------------------
// Hourly points
// ---------------------------------------------------------------------------

function rec(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

/** The stored withheld marker of a lean hourly point, if any. */
export function withheldMarker(point: unknown): "closed" | "no_lift_access" | null {
  const st = rec(point)?.st;
  return st === "closed" || st === "no_lift_access" ? st : null;
}

/** Stored lean points with withheld hours read as no score (a Closed hour never shows 0). */
export function withWithheldHoursNulled<T extends { score: number | null }>(
  points: readonly T[],
): (Omit<T, "score"> & { score: number | null })[] {
  return points.map((point) => (withheldMarker(point) ? { ...point, score: null } : point));
}

/** The engine's best-window length (hours) for a v3 sport: piste 4, off-piste and touring 3. */
export function skiV3WindowHours(sport: SkiSport): number {
  return sport === "piste" ? 4 : 3;
}
